/**
 * Sector Operations — Data Logger CSV Parser & Excursion Engine.
 * R39 M6.a (plan docs/plans/sector-operations.md §2 SO-P258).
 */

import { parseCsvText } from './csv.js'
import { zonedTimeToUtc, formatZonedDateKey } from '../charts/engine/bucket.js'
import type { AssetTempBand } from './assets.js'

export const MAX_TEMP_LOG_BYTES = 2 * 1024 * 1024 // 2 MB
export const MAX_TEMP_LOG_ROWS = 20000
export const EXCURSION_MERGE_WINDOW_MS = 30 * 60 * 1000 // 30 minutes

export interface TempLogColumnMapping {
  timestampColumn: string
  temperatureColumn: string
  probeColumn?: string
}

export interface TempLogParsedReading {
  timestampMs: number
  temperatureC: number
  probe?: string
  isExcursion: boolean
  dayKey: string
}

export interface TempExcursionWindow {
  windowStartMs: number
  windowEndMs: number
  minTemp: number
  maxTemp: number
  readingsCount: number
  probe?: string
}

export interface TempDaySummary {
  workspaceId: string
  assetId: string
  dayKey: string
  minTemp: number
  maxTemp: number
  readingCount: number
  excursions: TempExcursionWindow[]
  importIds: string[]
  updatedAt?: number
}

export interface ParseTempLogResult {
  readings: TempLogParsedReading[]
  totalReadings: number
  excursionCount: number
  windows: TempExcursionWindow[]
  daySummaries: Record<string, TempDaySummary>
}

/**
 * Parses timestamp string into UTC epoch millis in the specified workspace time zone.
 * Supports:
 * 1. ISO 8601 (with offset or in workspace time zone)
 * 2. DD-MM-YYYY HH:mm[:ss]
 * 3. DD/MM/YYYY HH:mm[:ss]
 */
export function parseTimestampInTz(raw: string, timeZone: string): number {
  const trimmed = raw.trim()
  if (!trimmed) {
    throw new Error('Empty timestamp string.')
  }

  // Format 2: DD-MM-YYYY HH:mm[:ss]
  const dmyDash = /^(\d{1,2})-(\d{1,2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(trimmed)
  if (dmyDash) {
    const day = Number(dmyDash[1])
    const month = Number(dmyDash[2])
    const year = Number(dmyDash[3])
    const hour = Number(dmyDash[4] || 0)
    const min = Number(dmyDash[5] || 0)
    const sec = Number(dmyDash[6] || 0)
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || min < 0 || min > 59 || sec < 0 || sec > 59) {
      throw new Error(`Invalid date/time components: "${trimmed}".`)
    }
    return zonedTimeToUtc(year, month, day, hour, min, sec, 0, timeZone).getTime()
  }

  // Format 3: DD/MM/YYYY HH:mm[:ss]
  const dmySlash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(trimmed)
  if (dmySlash) {
    const day = Number(dmySlash[1])
    const month = Number(dmySlash[2])
    const year = Number(dmySlash[3])
    const hour = Number(dmySlash[4] || 0)
    const min = Number(dmySlash[5] || 0)
    const sec = Number(dmySlash[6] || 0)
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || min < 0 || min > 59 || sec < 0 || sec > 59) {
      throw new Error(`Invalid date/time components: "${trimmed}".`)
    }
    return zonedTimeToUtc(year, month, day, hour, min, sec, 0, timeZone).getTime()
  }

  // Format 1: ISO 8601 with explicit offset or 'Z'
  if (/Z$|[+-]\d{2}(?::?\d{2})?$/.test(trimmed)) {
    const parsed = Date.parse(trimmed)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  // Format 1: ISO 8601 without offset: YYYY-MM-DD[T ]HH:mm[:ss]
  const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(trimmed)
  if (ymd) {
    const year = Number(ymd[1])
    const month = Number(ymd[2])
    const day = Number(ymd[3])
    const hour = Number(ymd[4] || 0)
    const min = Number(ymd[5] || 0)
    const sec = Number(ymd[6] || 0)
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 0 || hour > 23 || min < 0 || min > 59 || sec < 0 || sec > 59) {
      throw new Error(`Invalid date/time components: "${trimmed}".`)
    }
    return zonedTimeToUtc(year, month, day, hour, min, sec, 0, timeZone).getTime()
  }

  // Fallback
  const fallback = Date.parse(trimmed)
  if (!Number.isNaN(fallback)) {
    return fallback
  }

  throw new Error(
    `Unrecognized timestamp format: "${raw}". Expected ISO 8601, DD-MM-YYYY HH:mm, or DD/MM/YYYY HH:mm.`,
  )
}

/**
 * Pure parser for logger CSV files.
 * Validates columns, limits, parses timestamps and values, computes daily summaries,
 * and merges excursions within 30 minutes into windows.
 */
export function parseTempLogCsv(
  csvText: string,
  mapping: TempLogColumnMapping,
  tempBand: AssetTempBand,
  timeZone: string,
  workspaceId: string,
  assetId: string,
): ParseTempLogResult {
  if (!csvText || typeof csvText !== 'string' || !csvText.trim()) {
    throw new Error('CSV content cannot be empty.')
  }

  const { header, rows } = parseCsvText(csvText, {
    maxByteLength: MAX_TEMP_LOG_BYTES,
    maxDataRows: MAX_TEMP_LOG_ROWS,
  })

  if (!header || header.length === 0) {
    throw new Error('CSV must have a header row.')
  }

  if (rows.length === 0) {
    throw new Error('CSV has no data rows.')
  }

  if (rows.length > MAX_TEMP_LOG_ROWS) {
    throw new Error(`CSV exceeds maximum of ${MAX_TEMP_LOG_ROWS} rows.`)
  }

  const normHeader = header.map((h) => h.trim().toLowerCase())
  const tsColName = mapping.timestampColumn.trim().toLowerCase()
  const tempColName = mapping.temperatureColumn.trim().toLowerCase()
  const probeColName = mapping.probeColumn ? mapping.probeColumn.trim().toLowerCase() : undefined

  const tsIdx = normHeader.indexOf(tsColName)
  if (tsIdx === -1) {
    throw new Error(
      `Column "${mapping.timestampColumn}" not found in CSV header. Available columns: ${header.join(', ')}.`,
    )
  }

  const tempIdx = normHeader.indexOf(tempColName)
  if (tempIdx === -1) {
    throw new Error(
      `Column "${mapping.temperatureColumn}" not found in CSV header. Available columns: ${header.join(', ')}.`,
    )
  }

  const probeIdx = probeColName ? normHeader.indexOf(probeColName) : -1

  const parsedReadings: TempLogParsedReading[] = []

  for (const row of rows) {
    const rawTs = row.cells[tsIdx]
    const rawTemp = row.cells[tempIdx]
    if (!rawTs || !rawTemp) {
      continue
    }

    const timestampMs = parseTimestampInTz(rawTs, timeZone)
    const temperatureC = Number(rawTemp.trim())
    if (Number.isNaN(temperatureC)) {
      throw new Error(`Row ${row.rowNumber}: invalid temperature value "${rawTemp}".`)
    }

    const probe = probeIdx >= 0 && row.cells[probeIdx] ? row.cells[probeIdx].trim() : undefined
    const isExcursion = temperatureC < tempBand.min || temperatureC > tempBand.max
    const dayKey = formatZonedDateKey(new Date(timestampMs), timeZone)

    parsedReadings.push({
      timestampMs,
      temperatureC,
      ...(probe ? { probe } : {}),
      isExcursion,
      dayKey,
    })
  }

  // Sort chronologically
  parsedReadings.sort((a, b) => a.timestampMs - b.timestampMs)

  // 30-minute excursion window merge
  const windows: TempExcursionWindow[] = []
  let currentWindow: TempExcursionWindow | null = null

  for (const reading of parsedReadings) {
    if (!reading.isExcursion) {
      continue
    }

    if (!currentWindow) {
      currentWindow = {
        windowStartMs: reading.timestampMs,
        windowEndMs: reading.timestampMs,
        minTemp: reading.temperatureC,
        maxTemp: reading.temperatureC,
        readingsCount: 1,
        ...(reading.probe ? { probe: reading.probe } : {}),
      }
    } else {
      const gap = reading.timestampMs - currentWindow.windowEndMs
      if (gap <= EXCURSION_MERGE_WINDOW_MS) {
        currentWindow.windowEndMs = reading.timestampMs
        currentWindow.minTemp = Math.min(currentWindow.minTemp, reading.temperatureC)
        currentWindow.maxTemp = Math.max(currentWindow.maxTemp, reading.temperatureC)
        currentWindow.readingsCount++
      } else {
        windows.push(currentWindow)
        currentWindow = {
          windowStartMs: reading.timestampMs,
          windowEndMs: reading.timestampMs,
          minTemp: reading.temperatureC,
          maxTemp: reading.temperatureC,
          readingsCount: 1,
          ...(reading.probe ? { probe: reading.probe } : {}),
        }
      }
    }
  }

  if (currentWindow) {
    windows.push(currentWindow)
  }

  // Compute per-day summaries
  const daySummaries: Record<string, TempDaySummary> = {}
  for (const r of parsedReadings) {
    if (!daySummaries[r.dayKey]) {
      daySummaries[r.dayKey] = {
        workspaceId,
        assetId,
        dayKey: r.dayKey,
        minTemp: r.temperatureC,
        maxTemp: r.temperatureC,
        readingCount: 0,
        excursions: [],
        importIds: [],
      }
    }
    const day = daySummaries[r.dayKey]
    day.minTemp = Math.min(day.minTemp, r.temperatureC)
    day.maxTemp = Math.max(day.maxTemp, r.temperatureC)
    day.readingCount++
  }

  // Associate windows with days
  for (const w of windows) {
    const wDayKey = formatZonedDateKey(new Date(w.windowStartMs), timeZone)
    if (daySummaries[wDayKey]) {
      daySummaries[wDayKey].excursions.push(w)
    }
  }

  const excursionCount = parsedReadings.filter((r) => r.isExcursion).length

  return {
    readings: parsedReadings,
    totalReadings: parsedReadings.length,
    excursionCount,
    windows,
    daySummaries,
  }
}
